import { useCallback, useEffect, useMemo, useReducer } from "react";
import type { Edge } from "reactflow";
import type { GraphEdgeSelectionState } from "./GraphInfluenceEdge";

/**
 * The two-step highlight model used by the graph collection editor:
 *
 *   none → (click any edge) → neighborhood
 *      neighborhood → (click a highlighted edge) → focus
 *      neighborhood → (click the SAME anchor edge) → focus on that anchor
 *      neighborhood → (click an edge OUTSIDE the neighborhood) → new neighborhood
 *   any → (click pane / press Escape) → none
 *
 * "Neighborhood" = all edges whose source OR target is one of the clicked
 * edge's endpoints. This lets the user trace source/target chains from a
 * single click before narrowing focus to a specific edge.
 */
export type EdgeSelectionState =
  | { kind: "none" }
  | { kind: "neighborhood"; anchorEdgeId: string; edgeIds: Set<string>; nodeIds: Set<string> }
  | { kind: "focus"; edgeId: string; anchorEdgeId: string; neighborhoodEdgeIds: Set<string>; nodeIds: Set<string> };

export type EdgeSelectionAction =
  | { type: "edge_click"; edge: Edge<unknown> }
  | { type: "clear" }
  | { type: "prune"; existingEdgeIds: Set<string> };

type EdgeLike = Pick<Edge<unknown>, "id" | "source" | "target">;

/**
 * Compute the "neighborhood" edge ids around a clicked edge — every edge
 * whose source or target is one of the clicked edge's endpoints.
 */
export const computeNeighborhood = (
  allEdges: Array<EdgeLike>,
  anchorEdge: EdgeLike
): { edgeIds: Set<string>; nodeIds: Set<string> } => {
  const anchorNodes = new Set<string>([String(anchorEdge.source), String(anchorEdge.target)]);
  const edgeIds = new Set<string>();
  allEdges.forEach((edge) => {
    if (!edge || !edge.id) {
      return;
    }
    if (anchorNodes.has(String(edge.source)) || anchorNodes.has(String(edge.target))) {
      edgeIds.add(String(edge.id));
    }
  });
  edgeIds.add(String(anchorEdge.id));
  return { edgeIds, nodeIds: anchorNodes };
};

const initialState: EdgeSelectionState = { kind: "none" };

/**
 * Reducer used by `useEdgeSelection`. Kept as a plain pure function so it is
 * straightforward to unit-test without the hook wrapper.
 */
export const edgeSelectionReducer = (
  state: EdgeSelectionState,
  action: EdgeSelectionAction,
  allEdges: Array<EdgeLike> = []
): EdgeSelectionState => {
  if (action.type === "clear") {
    return state.kind === "none" ? state : { kind: "none" };
  }

  if (action.type === "prune") {
    if (state.kind === "none") {
      return state;
    }
    if (state.kind === "focus") {
      if (!action.existingEdgeIds.has(state.edgeId)) {
        return { kind: "none" };
      }
      const retainedNeighborhood = new Set<string>();
      state.neighborhoodEdgeIds.forEach((id) => {
        if (action.existingEdgeIds.has(id)) {
          retainedNeighborhood.add(id);
        }
      });
      return {
        ...state,
        neighborhoodEdgeIds: retainedNeighborhood,
      };
    }
    if (state.kind === "neighborhood") {
      if (!action.existingEdgeIds.has(state.anchorEdgeId)) {
        return { kind: "none" };
      }
      const retained = new Set<string>();
      state.edgeIds.forEach((id) => {
        if (action.existingEdgeIds.has(id)) {
          retained.add(id);
        }
      });
      return {
        ...state,
        edgeIds: retained,
      };
    }
    return state;
  }

  if (action.type === "edge_click") {
    const edge = action.edge;
    if (!edge?.id) {
      return state;
    }
    const edgeId = String(edge.id);

    if (state.kind === "neighborhood" && state.edgeIds.has(edgeId)) {
      // Second click inside the current neighborhood → focus that edge.
      return {
        kind: "focus",
        edgeId,
        anchorEdgeId: state.anchorEdgeId,
        neighborhoodEdgeIds: state.edgeIds,
        nodeIds: state.nodeIds,
      };
    }

    if (state.kind === "focus" && state.neighborhoodEdgeIds.has(edgeId)) {
      // Clicking a different edge in the same neighborhood → refocus to it.
      return {
        kind: "focus",
        edgeId,
        anchorEdgeId: state.anchorEdgeId,
        neighborhoodEdgeIds: state.neighborhoodEdgeIds,
        nodeIds: state.nodeIds,
      };
    }

    // Fresh click (or click outside the current neighborhood): compute new
    // neighborhood around the clicked edge.
    const { edgeIds, nodeIds } = computeNeighborhood(allEdges, edge);
    return {
      kind: "neighborhood",
      anchorEdgeId: edgeId,
      edgeIds,
      nodeIds,
    };
  }

  return state;
};

/**
 * Hook wrapper around `edgeSelectionReducer`. Returns a memoized map from
 * edge id → GraphEdgeSelectionState so callers can decorate their edges
 * without recomputing the mapping on each render.
 */
export const useEdgeSelection = (edges: Array<EdgeLike>): {
  state: EdgeSelectionState;
  selectionByEdgeId: Map<string, GraphEdgeSelectionState>;
  focusedEdgeId: string | null;
  neighborhoodNodeIds: Set<string>;
  handleEdgeClick: (edge: Edge<unknown>) => void;
  clear: () => void;
} => {
  // Hold the current edge list in a closure that the reducer can read. We
  // can't pass arbitrary extra args to dispatch, so we wrap dispatch in a
  // manual handler that invokes the reducer with the latest edges.
  const [state, dispatch] = useReducer(
    (current: EdgeSelectionState, action: EdgeSelectionAction): EdgeSelectionState =>
      edgeSelectionReducer(current, action, edges),
    initialState
  );

  // Keep the reducer's view of edges in sync by pruning when ids disappear.
  useEffect(() => {
    const existingEdgeIds = new Set(edges.map((edge) => String(edge.id)));
    dispatch({ type: "prune", existingEdgeIds });
  }, [edges]);

  const handleEdgeClick = useCallback(
    (edge: Edge<unknown>) => {
      dispatch({ type: "edge_click", edge });
    },
    []
  );

  const clear = useCallback(() => {
    dispatch({ type: "clear" });
  }, []);

  const selectionByEdgeId = useMemo(() => {
    const result = new Map<string, GraphEdgeSelectionState>();
    if (state.kind === "none") {
      edges.forEach((edge) => {
        if (edge?.id) {
          result.set(String(edge.id), "none");
        }
      });
      return result;
    }
    if (state.kind === "neighborhood") {
      edges.forEach((edge) => {
        if (!edge?.id) {
          return;
        }
        const id = String(edge.id);
        result.set(id, state.edgeIds.has(id) ? "neighborhood" : "dim");
      });
      return result;
    }
    // focus
    edges.forEach((edge) => {
      if (!edge?.id) {
        return;
      }
      const id = String(edge.id);
      if (id === state.edgeId) {
        result.set(id, "focus");
      } else if (state.neighborhoodEdgeIds.has(id)) {
        result.set(id, "neighborhood");
      } else {
        result.set(id, "dim");
      }
    });
    return result;
  }, [edges, state]);

  const focusedEdgeId = state.kind === "focus" ? state.edgeId : null;
  const neighborhoodNodeIds =
    state.kind === "neighborhood" || state.kind === "focus"
      ? state.nodeIds
      : new Set<string>();

  return {
    state,
    selectionByEdgeId,
    focusedEdgeId,
    neighborhoodNodeIds,
    handleEdgeClick,
    clear,
  };
};
